/**
 * StagingProcessingVisuals — the gap between an uploaded photo and a room to
 * furnish.
 *
 * Rooms are derived from the file hub's visual feed once each upload's RAW_WEB
 * derivative is ready, so a line can legitimately hold photos and no rooms for a
 * few seconds. This says so.
 *
 * It used to be the "nothing uploaded yet" state and its CTA re-opened the PRODUCT
 * DRAWER to upload — the coupling the source-visual move exists to remove, and the
 * reason a staging line could reach Save with nothing to stage. The upload now sits
 * directly above this module and the module is withheld until a visual arrives, so
 * the only remaining meaning is "uploaded, still processing": no CTA, because there
 * is nothing for the client to do but wait.
 *
 * ⚠️ The W-21 note this file used to carry — "a staging line with zero source
 * images is valid and orderable, do NOT demand an upload" — is DELIBERATELY
 * REVERSED (2026-09-15, product call). Staging is `EXACTLY_COUNT` on all four of
 * its variants, so the count is now derived from the upload and the configuration
 * is gated on it.
 */

import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'

import { BEIGE_700, GRAY_700, GRAY_900 } from 'constants/styling/theme'

/**
 * @component StagingProcessingVisuals
 * Shown while uploaded photos have not yet produced their rooms.
 */
export const StagingProcessingVisuals: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <Box
      sx={{
        px: '2.4rem',
        py: '4.8rem',
        border: `1px dashed ${BEIGE_700}`,
        borderRadius: '0.8rem',
      }}
    >
      <Stack gap="1.6rem" alignItems="center" justifyContent="center" textAlign="center">

        <HourglassEmptyOutlinedIcon sx={{ fontSize: '2.4rem', color: GRAY_700 }} />

        <Typography variant="text-md" fontWeight={500} color={GRAY_900}>
          {t('purchase_flow_revamp:configuration.staging.processing_visuals')}
        </Typography>

        <Typography variant="text-sm" color={GRAY_700}>
          {t('purchase_flow_revamp:configuration.staging.processing_visuals_subtitle')}
        </Typography>

      </Stack>
    </Box>
  )
}
